import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';
import { api } from '../api';
import type { UserAdminDetail } from '../types';

interface PatchPayload {
  is_active?: boolean;
  preferred_language?: string;
  email_alerts_enabled?: boolean;
  email_digest_enabled?: boolean;
}

interface DashboardPatch {
  coverage_mode?: string;
  fetch_frequency?: string;
}

type ConfirmAction = 'activate' | 'deactivate' | 'delete' | null;

export default function UserDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<UserAdminDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [patch, setPatch] = useState<PatchPayload>({});
  const [confirmAction, setConfirmAction] = useState<ConfirmAction>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState('');

  // Dashboard settings editor
  const [editingDashboard, setEditingDashboard] = useState<string | null>(null);
  const [dashboardPatch, setDashboardPatch] = useState<DashboardPatch>({});
  const [dashboardSaving, setDashboardSaving] = useState(false);
  const [dashboardSaveError, setDashboardSaveError] = useState('');

  useEffect(() => {
    if (!id) return;
    api<UserAdminDetail>(`/admin/users/${id}`)
      .then(u => { setUser(u); setPatch({ is_active: u.is_active }); })
      .catch(() => navigate('/users'))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  const handleSave = async () => {
    if (!id) return;
    setSaving(true);
    setSaveError('');
    try {
      const updated = await api<UserAdminDetail>(`/admin/users/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      });
      setUser(updated);
      setEditMode(false);
    } catch (e: unknown) {
      setSaveError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmedAction = async () => {
    if (!id || !confirmAction) return;
    setActionLoading(true);
    setActionError('');
    try {
      if (confirmAction === 'delete') {
        await api<void>(`/admin/users/${id}`, { method: 'DELETE' });
        navigate('/users');
        return;
      }
      const updated = await api<UserAdminDetail>(`/admin/users/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ is_active: confirmAction === 'activate' }),
      });
      setUser(updated);
      setConfirmAction(null);
    } catch (e: unknown) {
      setActionError(e instanceof Error ? e.message : 'Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDashboardSave = async (dashboardId: string) => {
    setDashboardSaving(true);
    setDashboardSaveError('');
    try {
      await api(`/admin/dashboards/${dashboardId}`, {
        method: 'PATCH',
        body: JSON.stringify(dashboardPatch),
      });
      // Refresh user detail to get updated dashboard list
      const updated = await api<UserAdminDetail>(`/admin/users/${id}`);
      setUser(updated);
      setEditingDashboard(null);
    } catch (e: unknown) {
      setDashboardSaveError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setDashboardSaving(false);
    }
  };

  if (loading) return <div className="loading-state"><div className="spinner" /></div>;
  if (!user) return null;

  const fmt = (dt: string | null) =>
    dt ? new Date(dt).toLocaleDateString('en', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

  const fmtDateTime = (dt: string | null) =>
    dt
      ? new Date(dt).toLocaleString('en', {
          month: 'short', day: 'numeric', year: 'numeric',
          hour: '2-digit', minute: '2-digit',
        })
      : '—';

  const initials = (user.full_name || user.email).slice(0, 2).toUpperCase();
  const lastActive = user.last_active_at || user.last_seen_at || user.last_login_at;

  const featureEntries = Object.entries(user.token_by_feature ?? {}).sort(
    ([, a], [, b]) => b.tokens - a.tokens
  );
  const maxFeatureTokens = featureEntries[0]?.[1].tokens || 1;

  return (
    <div className="page-content">
      <button className="back-link" onClick={() => navigate('/users')}>← Back to Users</button>

      {/* ── Confirmation dialog ───────────────────────────────────── */}
      {confirmAction && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 100,
          background: 'rgba(0,0,0,.35)', backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            background: 'var(--bg)', border: '1px solid var(--border)',
            borderRadius: 'var(--r-xl)', padding: 28, width: 400, maxWidth: '90vw',
            boxShadow: '0 20px 60px rgba(0,0,0,.18)',
          }}>
            <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--tx)', marginBottom: 8 }}>
              {confirmAction === 'delete' && 'Delete user?'}
              {confirmAction === 'deactivate' && 'Suspend user?'}
              {confirmAction === 'activate' && 'Reactivate user?'}
            </div>
            <div style={{ fontSize: 13.5, color: 'var(--tx2)', marginBottom: 20, lineHeight: 1.6 }}>
              {confirmAction === 'delete' && (
                <>This will <strong>permanently delete</strong> {user.email} and all their data including dashboards, feeds and notifications. This cannot be undone.</>
              )}
              {confirmAction === 'deactivate' && (
                <>This will immediately revoke <strong>{user.email}</strong>'s access. They will not be able to log in until reactivated.</>
              )}
              {confirmAction === 'activate' && (
                <>This will restore access for <strong>{user.email}</strong>.</>
              )}
            </div>
            {actionError && (
              <div style={{ fontSize: 13, color: 'var(--alert)', marginBottom: 14 }}>{actionError}</div>
            )}
            <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn-g" onClick={() => { setConfirmAction(null); setActionError(''); }}>
                Cancel
              </button>
              <button
                className={confirmAction === 'delete' ? 'btn-danger' : 'btn-p'}
                onClick={handleConfirmedAction}
                disabled={actionLoading}
              >
                {actionLoading ? 'Processing…' : (
                  confirmAction === 'delete' ? 'Delete permanently' :
                  confirmAction === 'deactivate' ? 'Suspend' : 'Reactivate'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Profile header ────────────────────────────────────────── */}
      <div className="profile-header">
        <div className="profile-avatar">{initials}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="row" style={{ marginBottom: 4, flexWrap: 'wrap' }}>
            <span className="profile-name">{user.full_name || user.email}</span>
            {user.is_active
              ? <span className="badge badge-ok">Active</span>
              : <span className="badge badge-alert">Suspended</span>}
            {user.is_superuser && <span className="badge badge-accent">Superuser</span>}
          </div>
          <div className="profile-email">{user.email}</div>
          <div style={{ fontSize: 12, color: 'var(--tx3)', marginTop: 4 }}>
            Joined {fmt(user.created_at)} · Last active {fmt(lastActive)}
          </div>
        </div>
        <div style={{ flexShrink: 0, display: 'flex', gap: 8, alignItems: 'center' }}>
          {!editMode ? (
            <>
              {user.is_active ? (
                <button className="btn-s" onClick={() => setConfirmAction('deactivate')}
                  title="Suspend account" style={{ color: 'var(--spiking)', borderColor: 'var(--spiking)' }}>
                  Suspend
                </button>
              ) : (
                <button className="btn-s" onClick={() => setConfirmAction('activate')}
                  title="Reactivate account" style={{ color: 'var(--ok)', borderColor: 'var(--ok)' }}>
                  Reactivate
                </button>
              )}
              <button className="btn-s" onClick={() => setEditMode(true)}>Edit</button>
              <button className="btn-danger" onClick={() => setConfirmAction('delete')} title="Delete user">
                Delete
              </button>
            </>
          ) : (
            <div className="row">
              <button className="btn-g" onClick={() => { setEditMode(false); setSaveError(''); }}>Cancel</button>
              <button className="btn-p" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── KPI row ────────────────────────────────────────────────── */}
      <div className="kpi-grid kpi-grid-5" style={{ marginBottom: 24 }}>
        <div className="kpi-cell">
          <div className="kpi-label">Dashboards</div>
          <div className="kpi-value">{user.dashboard_count}</div>
        </div>
        <div className="kpi-cell">
          <div className="kpi-label">Tokens In</div>
          <div className="kpi-value accent">{(user.tokens_in ?? 0).toLocaleString()}</div>
        </div>
        <div className="kpi-cell">
          <div className="kpi-label">Tokens Out</div>
          <div className="kpi-value accent">{(user.tokens_out ?? 0).toLocaleString()}</div>
        </div>
        <div className="kpi-cell">
          <div className="kpi-label">Est. Cost</div>
          <div className="kpi-value alert">${(user.total_cost_usd ?? 0).toFixed(4)}</div>
        </div>
        <div className="kpi-cell">
          <div className="kpi-label">Language</div>
          <div className="kpi-value" style={{ fontSize: 16, textTransform: 'uppercase' }}>
            {user.settings?.preferred_language || '—'}
          </div>
        </div>
      </div>

      {/* ── Edit form ─────────────────────────────────────────────── */}
      {editMode && (
        <div className="card" style={{ marginBottom: 24 }}>
          <div className="card-title">Edit User</div>
          {saveError && <div className="error-msg">{saveError}</div>}

          <div className="toggle-row">
            <div>
              <div className="toggle-label">Account Active</div>
              <div className="toggle-label" style={{ color: 'var(--tx3)', fontSize: 12 }}>
                Uncheck to suspend access immediately
              </div>
            </div>
            <input
              type="checkbox"
              style={{ width: 16, height: 16, cursor: 'pointer', accentColor: 'var(--accent)' }}
              checked={patch.is_active ?? user.is_active}
              onChange={e => setPatch(p => ({ ...p, is_active: e.target.checked }))}
            />
          </div>

          <div style={{ marginTop: 12, marginBottom: 12 }}>
            <label className="input-label">Preferred Language</label>
            <select
              className="select-input"
              value={patch.preferred_language ?? user.settings?.preferred_language ?? ''}
              onChange={e => setPatch(p => ({ ...p, preferred_language: e.target.value }))}
            >
              <option value="">Not set</option>
              <option value="az">Azerbaijani</option>
              <option value="en">English</option>
              <option value="ru">Russian</option>
              <option value="tr">Turkish</option>
            </select>
          </div>

          <div className="toggle-row">
            <div className="toggle-label">Email Alerts</div>
            <input
              type="checkbox"
              style={{ width: 16, height: 16, cursor: 'pointer', accentColor: 'var(--accent)' }}
              checked={patch.email_alerts_enabled ?? user.settings?.email_alerts_enabled ?? false}
              onChange={e => setPatch(p => ({ ...p, email_alerts_enabled: e.target.checked }))}
            />
          </div>

          <div className="toggle-row" style={{ borderBottom: 'none' }}>
            <div className="toggle-label">Email Digest</div>
            <input
              type="checkbox"
              style={{ width: 16, height: 16, cursor: 'pointer', accentColor: 'var(--accent)' }}
              checked={patch.email_digest_enabled ?? user.settings?.email_digest_enabled ?? false}
              onChange={e => setPatch(p => ({ ...p, email_digest_enabled: e.target.checked }))}
            />
          </div>
        </div>
      )}

      {/* ── Token chart (stacked in/out) ───────────────────────────── */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div className="card-title">Token Usage — Last 30 Days</div>
        {user.token_usage_30d.length ? (
          <>
            <div className="legend">
              <span className="legend-label">
                <span className="legend-dot" style={{ background: 'var(--accent)' }} />
                Input tokens
              </span>
              <span className="legend-label">
                <span className="legend-dot" style={{ background: 'var(--rising)' }} />
                Output tokens
              </span>
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={user.token_usage_30d} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-s)" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: 'var(--tx3)' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: 'var(--tx3)' }} axisLine={false} tickLine={false} />
                <Tooltip
                  contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid var(--border)', boxShadow: 'none' }}
                  cursor={{ fill: 'var(--bg3)' }}
                />
                <Bar dataKey="tokens_in" name="Input" stackId="a" fill="var(--accent)" />
                <Bar dataKey="tokens_out" name="Output" stackId="a" fill="var(--rising)" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </>
        ) : (
          <div className="empty-state">No token activity in the last 30 days</div>
        )}
      </div>

      {/* ── Token by feature ──────────────────────────────────────── */}
      {featureEntries.length > 0 && (
        <div className="card" style={{ marginBottom: 24 }}>
          <div className="card-title">Usage by Feature</div>
          <table className="data-table">
            <thead>
              <tr>
                <th>Feature</th>
                <th>Tokens In</th>
                <th>Tokens Out</th>
                <th>Total</th>
                <th>Cost</th>
                <th>Share</th>
              </tr>
            </thead>
            <tbody>
              {featureEntries.map(([feature, f]) => (
                <tr key={feature}>
                  <td>
                    <span className="tag" style={{ textTransform: 'none' }}>
                      {feature.replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td style={{ fontVariantNumeric: 'tabular-nums', fontSize: 12 }}>
                    {f.tokens_in.toLocaleString()}
                  </td>
                  <td style={{ fontVariantNumeric: 'tabular-nums', fontSize: 12 }}>
                    {f.tokens_out.toLocaleString()}
                  </td>
                  <td style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {f.tokens.toLocaleString()}
                  </td>
                  <td style={{ fontVariantNumeric: 'tabular-nums', color: 'var(--alert)' }}>
                    ${f.cost.toFixed(4)}
                  </td>
                  <td>
                    <div className="feature-bar">
                      <div className="feature-bar-track">
                        <div
                          className="feature-bar-fill"
                          style={{ width: `${Math.round((f.tokens / maxFeatureTokens) * 100)}%` }}
                        />
                      </div>
                      <span style={{ fontSize: 12, color: 'var(--tx2)' }}>
                        {((f.tokens / maxFeatureTokens) * 100).toFixed(0)}%
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Dashboards ────────────────────────────────────────────── */}
      {user.dashboards.length > 0 && (
        <div className="card" style={{ marginBottom: 24 }}>
          <div className="card-title">Dashboards ({user.dashboards.length})</div>
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Lang</th>
                <th>Fetch Frequency</th>
                <th>Strictness</th>
                <th>Briefings (7d)</th>
                <th>Created</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {user.dashboards.map(d => (
                <React.Fragment key={d.id}>
                  <tr
                    className="clickable"
                    onClick={() => navigate(`/dashboards/${d.id}`)}
                  >
                    <td style={{ fontWeight: 500 }}>{d.name}</td>
                    <td><span className="tag">{d.extraction_language.toUpperCase()}</span></td>
                    <td>
                      {d.fetch_enabled
                        ? <span className="badge badge-ok">{d.fetch_frequency || 'on'}</span>
                        : <span className="badge badge-neutral">Off</span>}
                    </td>
                    <td>
                      {d.coverage_mode
                        ? <span className="tag" style={{ textTransform: 'capitalize' }}>{d.coverage_mode}</span>
                        : <span style={{ color: 'var(--tx3)', fontSize: 12 }}>—</span>}
                    </td>
                    <td>{d.briefing_count_7d ?? 0}</td>
                    <td style={{ color: 'var(--tx3)' }}>{fmt(d.created_at)}</td>
                    <td>
                      <button
                        className="btn-s"
                        style={{ fontSize: 11 }}
                        onClick={e => {
                          e.stopPropagation();
                          setEditingDashboard(editingDashboard === d.id ? null : d.id);
                          setDashboardPatch({
                            coverage_mode: d.coverage_mode ?? 'balanced',
                            fetch_frequency: d.fetch_frequency || 'disabled',
                          });
                          setDashboardSaveError('');
                        }}
                      >
                        {editingDashboard === d.id ? 'Cancel' : 'Edit'}
                      </button>
                    </td>
                  </tr>
                  {editingDashboard === d.id && (
                    <tr>
                      <td colSpan={7} style={{ padding: 0 }}>
                        <div style={{
                          background: 'var(--bg2)',
                          border: '1px solid var(--border-s)',
                          borderRadius: 'var(--r)',
                          padding: '16px 20px',
                          margin: '0 0 2px',
                          display: 'flex',
                          alignItems: 'flex-end',
                          gap: 16,
                          flexWrap: 'wrap',
                        }}>
                          <div>
                            <label className="input-label" style={{ marginBottom: 4, display: 'block' }}>
                              Fetch Frequency
                            </label>
                            <select
                              className="select-input"
                              style={{ minWidth: 160 }}
                              value={dashboardPatch.fetch_frequency ?? 'disabled'}
                              onChange={e => setDashboardPatch(p => ({ ...p, fetch_frequency: e.target.value }))}
                            >
                              <option value="disabled">Disabled</option>
                              <option value="5min">Every 5 minutes</option>
                              <option value="15min">Every 15 minutes</option>
                              <option value="30min">Every 30 minutes</option>
                              <option value="hourly">Hourly</option>
                              <option value="daily">Daily</option>
                            </select>
                          </div>
                          <div>
                            <label className="input-label" style={{ marginBottom: 4, display: 'block' }}>
                              Coverage Strictness
                            </label>
                            <select
                              className="select-input"
                              style={{ minWidth: 160 }}
                              value={dashboardPatch.coverage_mode ?? 'balanced'}
                              onChange={e => setDashboardPatch(p => ({ ...p, coverage_mode: e.target.value }))}
                            >
                              <option value="strict">Strict (≥80% match)</option>
                              <option value="balanced">Balanced (≥70% match)</option>
                              <option value="broad">Broad (≥60% match)</option>
                            </select>
                          </div>
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            {dashboardSaveError && (
                              <span style={{ fontSize: 12, color: 'var(--alert)' }}>{dashboardSaveError}</span>
                            )}
                            <button
                              className="btn-p"
                              style={{ fontSize: 12 }}
                              disabled={dashboardSaving}
                              onClick={() => handleDashboardSave(d.id)}
                            >
                              {dashboardSaving ? 'Saving…' : 'Save'}
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Email / notification history ──────────────────────────── */}
      {user.email_history?.length > 0 && (
        <div className="card">
          <div className="card-title">Recent Notifications</div>
          <table className="data-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Type</th>
                <th>Title</th>
                <th>Read</th>
              </tr>
            </thead>
            <tbody>
              {user.email_history.map(n => (
                <tr key={n.id}>
                  <td style={{ fontSize: 12, color: 'var(--tx3)', whiteSpace: 'nowrap' }}>
                    {fmtDateTime(n.created_at)}
                  </td>
                  <td>
                    <span className="tag" style={{ textTransform: 'none' }}>{n.type}</span>
                  </td>
                  <td style={{ fontWeight: 500 }}>{n.title}</td>
                  <td>
                    {n.is_read
                      ? <span className="badge badge-neutral">Read</span>
                      : <span className="badge badge-accent">Unread</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
